import { generatePath, matchPath } from "react-router-dom";

/**
 * Every Karkhana address in one place. Sections mirror the More page, so the address bar says where
 * you are: /karkhana/floor/rounds, /karkhana/money/pay-sheets/…, /karkhana/rates/rate-cards/….
 * Old addresses (emails, bookmarks, the installed app) keep working through LEGACY_REDIRECTS.
 */
export type KarkhanaSection = "" | "Floor" | "Sarees" | "Money" | "People" | "Rates" | "Tools";

/** Route patterns, in the order the router must try them (fixed words before `:id`). */
export const KP = {
  home: "/karkhana",
  more: "/karkhana/more",
  settings: "/karkhana/settings",
  login: "/karkhana/login",
  // Floor (daily)
  rounds: "/karkhana/floor/rounds",
  dailyWork: "/karkhana/floor/daily-work",
  attendance: "/karkhana/floor/attendance",
  jobWork: "/karkhana/floor/job-work",
  // Sarees & checks
  sarees: "/karkhana/sarees",
  saree: "/karkhana/sarees/:id",
  approvals: "/karkhana/approvals",
  loom: "/karkhana/looms/:id",
  // Money
  pay: "/karkhana/money/pay",
  paySheets: "/karkhana/money/pay-sheets",
  paySheet: "/karkhana/money/pay-sheets/:id",
  payOpenings: "/karkhana/money/opening-balances",
  cashBook: "/karkhana/money/cash-book",
  // Cash book v1 (CASHBOOK add-on); the cash-book page itself picks the old or new screen.
  cashCamera: "/karkhana/money/cash-book/camera",
  cashSpend: "/karkhana/money/cash-book/spend",
  cashEntry: "/karkhana/money/cash-book/entry/:type/:id",
  cashGive: "/karkhana/money/cash-book/give",
  cashTransfer: "/karkhana/money/cash-book/transfer/:id",
  cashPrint: "/karkhana/money/cash-book/print/:printId",
  cashCheck: "/karkhana/money/cash-book/check/:printId",
  cashSpendsCheck: "/karkhana/money/cash-book/spends/:photoId",
  cashWorker: "/karkhana/money/cash-book/worker/:personId",
  cashHistory: "/karkhana/money/cash-book/history",
  cashHeads: "/karkhana/money/cash-book/spend-types",
  cashHolder: "/karkhana/money/cash-book/supervisor/:userId",
  costings: "/karkhana/money/costings",
  costing: "/karkhana/money/costings/:id",
  // People
  workers: "/karkhana/people/workers",
  workerImport: "/karkhana/people/workers/import",
  workerPhotos: "/karkhana/people/workers/photos",
  workerReview: "/karkhana/people/workers/review",
  supervisors: "/karkhana/people/supervisors",
  codes: "/karkhana/people/codes",
  // Designs & rates
  designs: "/karkhana/rates/designs",
  rateCards: "/karkhana/rates/rate-cards",
  rateImport: "/karkhana/rates/rate-cards/import",
  rateCard: "/karkhana/rates/rate-cards/:id",
  timeRates: "/karkhana/rates/time-rates",
  parts: "/karkhana/rates/parts",
  costingLines: "/karkhana/rates/costing-lines",
  // Tools & help
  import: "/karkhana/tools/import",
  oldSheet: "/karkhana/tools/old-sheet",
  reports: "/karkhana/tools/reports",
  guide: "/karkhana/tools/guide",
} as const;

export type KarkhanaPageKey = Exclude<keyof typeof KP, "login">;

/**
 * Each signed-in Karkhana page: its route, the name in the browser tab ("Rounds · Floor · Karkhana")
 * and the name the studio top bar showed before (kept as it was).
 */
export const KARKHANA_PAGES: { key: KarkhanaPageKey; screen: string; section: KarkhanaSection; bar: string }[] = [
  { key: "dailyWork", screen: "Daily work", section: "Floor", bar: "Daily work" },
  { key: "attendance", screen: "Attendance", section: "Floor", bar: "Attendance" },
  { key: "rounds", screen: "Rounds", section: "Floor", bar: "Rounds" },
  { key: "jobWork", screen: "Job work", section: "Floor", bar: "Job work" },
  { key: "sarees", screen: "Sarees", section: "Sarees", bar: "Sarees" },
  { key: "saree", screen: "Saree", section: "Sarees", bar: "Sarees" },
  { key: "approvals", screen: "Approvals", section: "Sarees", bar: "Approvals" },
  { key: "loom", screen: "Loom", section: "Sarees", bar: "Loom history" },
  { key: "pay", screen: "Pay", section: "Money", bar: "Pay" },
  { key: "paySheets", screen: "Pay sheets", section: "Money", bar: "Pay sheets" },
  { key: "paySheet", screen: "Pay sheet", section: "Money", bar: "Pay sheets" },
  { key: "payOpenings", screen: "Opening balances", section: "Money", bar: "Opening balances" },
  { key: "cashBook", screen: "Cash book", section: "Money", bar: "Cash book" },
  { key: "cashCamera", screen: "Cash camera", section: "Money", bar: "Cash book" },
  { key: "cashSpend", screen: "Spend", section: "Money", bar: "Cash book" },
  { key: "cashEntry", screen: "Entry", section: "Money", bar: "Cash book" },
  { key: "cashGive", screen: "Give cash", section: "Money", bar: "Cash book" },
  { key: "cashTransfer", screen: "Handover", section: "Money", bar: "Cash book" },
  { key: "cashPrint", screen: "Print", section: "Money", bar: "Cash book" },
  { key: "cashCheck", screen: "Sheet check", section: "Money", bar: "Cash book" },
  { key: "cashSpendsCheck", screen: "Spends check", section: "Money", bar: "Cash book" },
  { key: "cashWorker", screen: "Worker payments", section: "Money", bar: "Cash book" },
  { key: "cashHistory", screen: "Checked history", section: "Money", bar: "Cash book" },
  { key: "cashHeads", screen: "Spend types", section: "Money", bar: "Cash book" },
  { key: "cashHolder", screen: "Supervisor cash", section: "Money", bar: "Cash book" },
  { key: "costings", screen: "Costings", section: "Money", bar: "Costing" },
  { key: "costing", screen: "Costing", section: "Money", bar: "Karkhana" },
  { key: "workers", screen: "Workers", section: "People", bar: "Workers" },
  { key: "workerImport", screen: "Import workers", section: "People", bar: "Import workers" },
  { key: "workerPhotos", screen: "Worker photos", section: "People", bar: "Karkhana" },
  { key: "workerReview", screen: "Workers to review", section: "People", bar: "Workers to review" },
  { key: "supervisors", screen: "Supervisors", section: "People", bar: "Supervisors" },
  { key: "codes", screen: "Codes", section: "People", bar: "Categories & codes" },
  { key: "designs", screen: "Designs", section: "Rates", bar: "Designs" },
  { key: "rateCards", screen: "Rate cards", section: "Rates", bar: "Rate cards" },
  { key: "rateImport", screen: "Import designs and rates", section: "Rates", bar: "Import designs and rates" },
  { key: "rateCard", screen: "Rate card", section: "Rates", bar: "Rate cards" },
  { key: "timeRates", screen: "Time rates", section: "Rates", bar: "Time rates" },
  { key: "parts", screen: "Saree parts", section: "Rates", bar: "Saree parts" },
  { key: "costingLines", screen: "Costing lines", section: "Rates", bar: "Costing lines and rates" },
  { key: "import", screen: "Import", section: "Tools", bar: "Karkhana" },
  { key: "oldSheet", screen: "Old sheet", section: "Tools", bar: "Import the old sheet" },
  { key: "reports", screen: "Reports", section: "Tools", bar: "Reports" },
  { key: "guide", screen: "How it works", section: "Tools", bar: "How Karkhana works" },
  { key: "settings", screen: "Settings", section: "", bar: "Karkhana settings" },
  { key: "more", screen: "More", section: "", bar: "Karkhana" },
  { key: "home", screen: "Karkhana", section: "", bar: "Karkhana" },
];

/** Old address → new one. `:id` carries across; the query and #hash are kept by the redirect. */
export const LEGACY_REDIRECTS: [string, string][] = [
  ["/karkhana/rounds", KP.rounds],
  ["/karkhana/daily-work", KP.dailyWork],
  ["/karkhana/attendance", KP.attendance],
  ["/karkhana/job-work", KP.jobWork],
  ["/karkhana/looms/:id/history", KP.loom],
  ["/karkhana/pay", KP.pay],
  ["/karkhana/pay-sheets", KP.paySheets],
  ["/karkhana/pay-sheets/:id", KP.paySheet],
  ["/karkhana/cash-book", KP.cashBook],
  ["/karkhana/costings", KP.costings],
  ["/karkhana/costings/:id", KP.costing],
  ["/karkhana/workers", KP.workers],
  ["/karkhana/workers/import", KP.workerImport],
  ["/karkhana/workers/photos", KP.workerPhotos],
  ["/karkhana/workers/review", KP.workerReview],
  ["/karkhana/workers/categories", KP.codes],
  ["/karkhana/supervisors", KP.supervisors],
  ["/karkhana/designs", KP.designs],
  ["/karkhana/rate-cards", KP.rateCards],
  ["/karkhana/rate-cards/import", KP.rateImport],
  ["/karkhana/rate-cards/:id", KP.rateCard],
  ["/karkhana/time-rates", KP.timeRates],
  ["/karkhana/parts", KP.parts],
  ["/karkhana/costing-setup", KP.costingLines],
  ["/karkhana/import", KP.import],
  ["/karkhana/history-import", KP.oldSheet],
  ["/karkhana/reports", KP.reports],
  ["/karkhana/guide", KP.guide],
];

type Query = Record<string, string | number | null | undefined>;

// encodeURIComponent (space → %20), so links already sent out keep their exact form.
const query = (q?: Query, hash?: string) => {
  const s = Object.entries(q || {})
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");
  return `${s ? `?${s}` : ""}${hash ? `#${hash}` : ""}`;
};

const at = (path: string) => (q?: Query, hash?: string) => `${path}${query(q, hash)}`;
const withId = (pattern: string) => (id: string, q?: Query, hash?: string) =>
  `${pattern.replace(":id", encodeURIComponent(id))}${query(q, hash)}`;
/** A pattern with one named parameter other than :id (":printId", ":photoId"…). */
const withParam = (pattern: string) => (value: string, q?: Query, hash?: string) =>
  `${pattern.replace(/:[A-Za-z]+/, encodeURIComponent(value))}${query(q, hash)}`;

/** Address builders: kp.rounds({ date, loom }), kp.saree(id), kp.pay({ person }, "not-counted"). */
export const kp = {
  home: at(KP.home),
  more: at(KP.more),
  settings: at(KP.settings),
  login: at(KP.login),
  rounds: at(KP.rounds),
  dailyWork: at(KP.dailyWork),
  attendance: at(KP.attendance),
  jobWork: at(KP.jobWork),
  sarees: at(KP.sarees),
  saree: withId(KP.saree),
  approvals: at(KP.approvals),
  loom: withId(KP.loom),
  pay: at(KP.pay),
  paySheets: at(KP.paySheets),
  payOpenings: at(KP.payOpenings),
  paySheet: withId(KP.paySheet),
  cashBook: at(KP.cashBook),
  cashCamera: (q?: { kind?: "PAY_SHEET" | "SPENDS"; printId?: string | null; page?: number | null; date?: string | null }) => at(KP.cashCamera)(q),
  cashSpend: at(KP.cashSpend),
  cashEntry: (type: string, id: string, q?: Query) =>
    `${KP.cashEntry.replace(":type", encodeURIComponent(type)).replace(":id", encodeURIComponent(id))}${query(q)}`,
  cashGive: at(KP.cashGive),
  cashTransfer: withId(KP.cashTransfer),
  cashPrint: withParam(KP.cashPrint),
  cashCheck: withParam(KP.cashCheck),
  cashSpendsCheck: withParam(KP.cashSpendsCheck),
  cashWorker: withParam(KP.cashWorker),
  cashHistory: at(KP.cashHistory),
  cashHeads: at(KP.cashHeads),
  cashHolder: withParam(KP.cashHolder),
  costings: at(KP.costings),
  costing: withId(KP.costing),
  workers: at(KP.workers),
  workerImport: at(KP.workerImport),
  workerPhotos: at(KP.workerPhotos),
  workerReview: at(KP.workerReview),
  supervisors: at(KP.supervisors),
  codes: at(KP.codes),
  designs: at(KP.designs),
  rateCards: at(KP.rateCards),
  rateImport: at(KP.rateImport),
  rateCard: withId(KP.rateCard),
  timeRates: at(KP.timeRates),
  parts: at(KP.parts),
  costingLines: at(KP.costingLines),
  import: at(KP.import),
  oldSheet: at(KP.oldSheet),
  reports: at(KP.reports),
  guide: at(KP.guide),
};

const split = (address: string) => {
  const m = /^([^?#]*)(\?[^#]*)?(#.*)?$/.exec(address) || [];
  return { pathname: m[1] || "", search: m[2] || "", hash: m[3] || "" };
};

/**
 * The current address for an old one, keeping the id, query and hash ("/karkhana/costings/c1?x=1#y"
 * → "/karkhana/money/costings/c1?x=1#y"). Anything that is not an old address comes back unchanged.
 */
export const currentKarkhanaAddress = (address: string): string => {
  const { pathname, search, hash } = split(address);
  for (const [from, to] of LEGACY_REDIRECTS) {
    const m = matchPath<{ id?: string }>(pathname, { path: from, exact: true });
    if (m) return `${generatePath(to, m.params as any)}${search}${hash}`;
  }
  return address;
};

/** Which Karkhana page an address is, if any (first match in route order, like the router). */
export const karkhanaPageFor = (pathname: string) =>
  KARKHANA_PAGES.find((p) => matchPath(pathname, { path: KP[p.key], exact: true })) || null;

/** "Rounds · Floor · Karkhana"; a page can pass its own name ("Saree 18301"). */
export const karkhanaTitleFor = (pathname: string, screen?: string): string => {
  const page = karkhanaPageFor(pathname);
  if (!page || page.key === "home") return "Karkhana";
  return [screen || page.screen, page.section, "Karkhana"].filter(Boolean).join(" · ");
};
